import { Flex } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { ApiKeyCreateResponse, ApiKeyRead } from "@nens/rana/api-v1";
import { ModalProps, RanaButton, RanaLoading, RanaModal } from "@nens/ranalib";
import { FormErrors, RanaForm, SubmitFunction } from "@nens/ranalib";

import { BackButton } from "@/components/back-button";
import { useShareKeyListQuery } from "@/queries/query-share-key";
import { cancelButtonText } from "@/translations";

import { getId, getName } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";

import { useCreateShareKey } from "./api/create-share-key";
import { useEditShareKey } from "./api/edit-share-key";

type SharePublicationFormValues = {
  name: string;
  expiresAt: Date | null;
};

interface PublicationDetailShareCreateFormProps {
  onClose: () => void;
  setShareKey: (createdShareKey: ApiKeyCreateResponse) => void;
  title?: string;
}

export function PublicationDetailShareCreateForm({
  onClose,
  setShareKey,
  title,
}: PublicationDetailShareCreateFormProps) {
  const intl = useIntl();
  if (!title)
    title = intl.formatMessage({
      id: "reference.create_share",
      defaultMessage: "Share publication",
    });

  const publicationId = usePublicationStore(getId);
  const publicationName = usePublicationStore(getName);

  const { isMutating, mutate } = useCreateShareKey(publicationId);

  const { isLoading, data: shareKeysList } = useShareKeyListQuery({
    publicationId,
    limit: 1,
    orderBy: "-created_at",
  });

  const mostRecentShareKey = shareKeysList?.items[0];

  const initialValues: SharePublicationFormValues = {
    name: isLoading ? "" : mostRecentShareKey?.name ?? publicationName,
    expiresAt: mostRecentShareKey?.expiresAt ?? null,
  };

  const submit: SubmitFunction<SharePublicationFormValues> = async (values, onError) => {
    mutate(values, {
      onSuccess: setShareKey,
      onError,
    });
  };

  return (
    <PublicationDetailShareCreateOrEdit
      onClose={onClose}
      title={title}
      buttonTitle={title}
      submit={submit}
      initialValues={initialValues}
      isMutating={isMutating}
    />
  );
}

interface PublicationDetailShareEditProps {
  onClose: () => void;
  setRevokeShare: () => void;
  back: () => void;
  share: ApiKeyRead;
}

export function PublicationDetailShareEdit(props: PublicationDetailShareEditProps) {
  const { onClose, setRevokeShare, share, back } = props;
  const intl = useIntl();
  const title = intl.formatMessage({
    id: "reference.edit_share",
    defaultMessage: "Edit share",
  });

  const buttonTitle = intl.formatMessage({
    id: "save_changes",
    defaultMessage: "Save changes",
  });

  const publicationId = usePublicationStore(getId);

  const { isMutating, mutate } = useEditShareKey(publicationId, share.id);

  const initialValues: SharePublicationFormValues = {
    name: share.name,
    expiresAt: share.expiresAt,
  };

  const submit: SubmitFunction<SharePublicationFormValues> = async (values, onError) => {
    mutate(values, {
      onSuccess: () => {
        back();
      },
      onError,
    });
  };

  return (
    <PublicationDetailShareCreateOrEdit
      onClose={onClose}
      title={title}
      buttonTitle={buttonTitle}
      submit={submit}
      initialValues={initialValues}
      isMutating={isMutating}
      back={back}
      modalProps={{
        bottomChild: (
          <RanaButton variant="danger" onClick={setRevokeShare} isDisabled={isMutating}>
            {intl.formatMessage({
              id: "share.revoke_share",
              defaultMessage: "Revoke share",
            })}
          </RanaButton>
        ),
      }}
    />
  );
}

type PublicationDetailShareCreateOrEditProps = {
  title: string;
  buttonTitle: string;
  initialValues: SharePublicationFormValues;
  submit: SubmitFunction<SharePublicationFormValues>;
  isMutating: boolean;
  onClose: () => void;
  modalProps?: Partial<ModalProps>;
  back?: () => void;
};

function PublicationDetailShareCreateOrEdit({
  title,
  buttonTitle,
  submit,
  initialValues,
  isMutating,
  onClose,
  back,
  modalProps,
}: PublicationDetailShareCreateOrEditProps) {
  const intl = useIntl();

  const validate = ({ expiresAt }: SharePublicationFormValues) => {
    const errors: FormErrors = {};

    if (expiresAt && expiresAt < new Date())
      errors.expiresAt = intl.formatMessage({
        id: "form.expire.not.future",
        defaultMessage: "The selected expiry date has already passed. Please choose a future date.",
      });

    return errors;
  };

  return (
    <RanaForm initialValues={initialValues} validate={validate} doSubmit={submit}>
      <RanaModal
        isOpen
        onClose={onClose}
        topBar={title}
        maxW="xl"
        buttons={[
          {
            children: cancelButtonText,
            variant: "tertiary",
            isDisabled: isMutating,
            onClick: back ?? onClose,
          },
          <RanaForm.SubmitButton key={buttonTitle} isDisabled={isMutating}>
            {buttonTitle}
          </RanaForm.SubmitButton>,
        ]}
        {...modalProps}
      >
        {back && (
          <BackButton back={back}>
            <FormattedMessage id="publication.back" defaultMessage="back" />
          </BackButton>
        )}

        <Flex flexDir="column" gap={2}>
          <RanaForm.Input isRequired field="name" label="Name" />
          <RanaForm.Input type="date" field="expiresAt" label="Expires at" />
          <RanaForm.Errors />

          {isMutating && <RanaLoading size="md" isCentered />}
        </Flex>
      </RanaModal>
    </RanaForm>
  );
}
